import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { telegramApi } from '@/services/api/telegramApi';
import { extractError } from '@/lib/extractError';
import { isApiError } from '@/types';

/** How often we ask the backend whether the user has pressed Start yet. */
export const TELEGRAM_POLL_INTERVAL_MS = 3_000;

/**
 * How long we keep polling before telling the user to get a fresh link.
 *
 * Shorter than the backend's 10-minute code TTL on purpose: a user who has not
 * pressed Start within three minutes has almost certainly closed the tab, and
 * polling a code we cannot see expire is wasted load. The card's retry mints a
 * new code, so nothing is lost by giving up early.
 */
export const TELEGRAM_POLL_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Shown when the browser refuses the tab we claimed on the click. The code is
 * still good, so the card offers the deep link as a plain link the user can
 * open themselves.
 */
export const TELEGRAM_POPUP_BLOCKED =
  'Your browser blocked the Telegram tab. Use Open Telegram below to finish connecting.';

/** Shown when the backend hands back something that is not a Telegram link. */
export const TELEGRAM_BAD_LINK = 'Could not start the Telegram link. Try again.';

/**
 * Shown when the backend is deployed but this environment has no
 * `TELEGRAM_BOT_USERNAME`, which it reports as 503 `TELEGRAM_NOT_CONFIGURED`.
 *
 * Distinct copy because retrying can never fix it, and the backend's own
 * message ('Telegram is not configured on this environment') is operator
 * wording, not something to put in front of a user.
 */
export const TELEGRAM_NOT_CONFIGURED =
  'Telegram is not available on this environment yet. Check back once it is switched on.';

/** True for the 503 the backend raises when no bot handle is configured. */
function isNotConfigured(err: unknown): boolean {
  return isApiError(err) && err.error.code === 'TELEGRAM_NOT_CONFIGURED';
}

/**
 * The only destination we will navigate a tab to.
 *
 * The tab we open is `about:blank`, which inherits this origin, so assigning
 * `location.href` runs a `javascript:` payload as us. The URL comes from our own
 * backend, but the check costs nothing and keeps a compromised or mistaken
 * response from turning into script execution in the session's context.
 */
function isTelegramDeepLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname === 't.me' || parsed.hostname === 'telegram.me')
    );
  } catch {
    return false;
  }
}

/**
 * `idle` covers both "never linked" and "linked" — the `linked` flag carries
 * that. The phase only tracks an in-flight link attempt, which is what the
 * three card states render off.
 */
export type TelegramLinkPhase = 'idle' | 'connecting' | 'pending' | 'expired';

interface TelegramState {
  linked: boolean;
  username?: string;
  /**
   * The deep link of the attempt in flight, so the card can offer it as a real
   * link. A link click carries its own user activation, so it works even when
   * the tab we opened was blocked, and it reuses the live code rather than
   * minting another one.
   */
  linkUrl: string | null;
  phase: TelegramLinkPhase;
  /** False until a status read has resolved once, success or failure. */
  hydrated: boolean;
  isUnlinking: boolean;
  error: string | null;
}

interface TelegramActions {
  /** Read the current link status. Never regresses a known-linked account. */
  refresh: () => Promise<void>;
  /** Mint a code, open the deep link, and poll until Start lands. */
  connect: () => Promise<void>;
  /**
   * Run one poll tick immediately. Mounted against window focus and tab
   * visibility: on mobile the deep link switches to the Telegram app, and the
   * background timer is throttled while we are hidden, so the return to the
   * tab is the moment we most want to re-check.
   */
  checkNow: () => Promise<void>;
  unlink: () => Promise<void>;
  /** Drops all state and cancels any armed poll. */
  reset: () => void;
}

const initialState: TelegramState = {
  linked: false,
  username: undefined,
  linkUrl: null,
  phase: 'idle',
  hydrated: false,
  isUnlinking: false,
  error: null,
};

/**
 * The armed poll, kept outside the store: a timer handle is not render state,
 * and putting it in the store would make every tick a no-op re-render.
 */
let pollTimer: ReturnType<typeof setTimeout> | null = null;
/** Epoch ms after which the current attempt's code is treated as dead. */
let pollDeadline: number | null = null;
/** The tick currently reading status, so concurrent callers share one read. */
let inFlightTick: Promise<void> | null = null;

function cancelPoll(): void {
  if (pollTimer !== null) clearTimeout(pollTimer);
  pollTimer = null;
  pollDeadline = null;
}

export const useTelegramStore = create<TelegramState & TelegramActions>()(
  devtools(
    (set, get) => {
      /**
       * One poll tick. Returns once the attempt has resolved one way or the
       * other, so `checkNow` can be awaited in tests and in the visibility
       * handler.
       */
      const tick = async (): Promise<void> => {
        if (pollDeadline === null) return;
        // Focus and visibilitychange both fire on a tab return, and every
        // mounted card listens, so ticks would otherwise pile up on one moment.
        if (inFlightTick) return inFlightTick;

        inFlightTick = runTick();
        try {
          await inFlightTick;
        } finally {
          inFlightTick = null;
        }
      };

      /** The read itself. Always reached through {@link tick}. */
      const runTick = async (): Promise<void> => {
        try {
          const status = await telegramApi.getStatus();
          if (status.linked) {
            cancelPoll();
            set(
              {
                linked: true,
                username: status.username,
                linkUrl: null,
                phase: 'idle',
                hydrated: true,
                error: null,
              },
              false,
              'telegram/poll/linked'
            );
            return;
          }
        } catch {
          // A failed tick is not a failed link — the user may still be mid-Start.
          // Stay pending and try again on the next tick or the next deadline check.
        }

        // Checked after the read, not before: a Start that landed in the same
        // tick as the deadline should still count as a success.
        if (pollDeadline !== null && Date.now() >= pollDeadline) {
          cancelPoll();
          // The code behind it is dead, so the link must not linger in state
          // where a card, or devtools, could still hand it to someone.
          set({ phase: 'expired', linkUrl: null }, false, 'telegram/poll/expired');
          return;
        }

        arm();
      };

      /** Schedule the next tick, replacing any already-armed one. */
      const arm = (): void => {
        if (pollTimer !== null) clearTimeout(pollTimer);
        pollTimer = setTimeout(() => {
          void tick();
        }, TELEGRAM_POLL_INTERVAL_MS);
      };

      return {
        ...initialState,

        refresh: async () => {
          try {
            const status = await telegramApi.getStatus();
            // A read that lands while an attempt is in flight resolves it: the
            // poll has nothing left to wait for, and the deep link is spent.
            if (status.linked) cancelPoll();
            set(
              {
                linked: status.linked,
                username: status.username,
                ...(status.linked ? { phase: 'idle' as TelegramLinkPhase, linkUrl: null } : null),
                hydrated: true,
                error: null,
              },
              false,
              'telegram/refresh/success'
            );
          } catch {
            // Best effort, mirroring the setup store: a failed read leaves the
            // last known state rather than telling a linked user they are not.
            set({ hydrated: true }, false, 'telegram/refresh/error');
          }
        },

        connect: async () => {
          cancelPoll();
          set({ phase: 'connecting', error: null, linkUrl: null }, false, 'telegram/connect/start');

          // Claimed synchronously, still inside the click that called us:
          // Safari and others drop transient user activation across the network
          // round trip below, so opening the tab after the await gets blocked
          // even though a real click started the flow. We take a blank tab now
          // and point it at the deep link once we have one.
          const tab = window.open('', '_blank');
          // t.me must not inherit a handle on this window once we navigate.
          if (tab) tab.opener = null;

          let tMeUrl: string;
          let expiresAt: string;
          try {
            const code = await telegramApi.createLinkCode();
            tMeUrl = code.tMeUrl;
            expiresAt = code.expiresAt;
          } catch (err) {
            tab?.close();
            set(
              {
                phase: 'idle',
                error: isNotConfigured(err)
                  ? TELEGRAM_NOT_CONFIGURED
                  : extractError(err, 'Could not start the Telegram link. Try again.'),
              },
              false,
              isNotConfigured(err) ? 'telegram/connect/not-configured' : 'telegram/connect/error'
            );
            return;
          }

          if (!isTelegramDeepLink(tMeUrl)) {
            tab?.close();
            set({ phase: 'idle', error: TELEGRAM_BAD_LINK }, false, 'telegram/connect/bad-link');
            return;
          }

          if (tab) tab.location.href = tMeUrl;

          // Never poll past the code's own expiry, and never longer than our
          // own give-up window, whichever comes first.
          const codeExpiry = Date.parse(expiresAt);
          pollDeadline = Math.min(
            Number.isNaN(codeExpiry) ? Infinity : codeExpiry,
            Date.now() + TELEGRAM_POLL_TIMEOUT_MS
          );

          // A blocked tab is still a live attempt: the code is minted and the
          // card hands the user the link, so we poll either way and only the
          // message differs.
          set(
            { phase: 'pending', linkUrl: tMeUrl, error: tab ? null : TELEGRAM_POPUP_BLOCKED },
            false,
            tab ? 'telegram/connect/pending' : 'telegram/connect/popup-blocked'
          );
          arm();
        },

        checkNow: async () => {
          if (get().phase !== 'pending') return;
          await tick();
        },

        unlink: async () => {
          cancelPoll();
          set({ isUnlinking: true, error: null }, false, 'telegram/unlink/start');
          try {
            await telegramApi.unlink();
            set(
              {
                linked: false,
                username: undefined,
                linkUrl: null,
                phase: 'idle',
                isUnlinking: false,
              },
              false,
              'telegram/unlink/success'
            );
          } catch (err) {
            set(
              {
                isUnlinking: false,
                error: extractError(err, 'Could not disconnect Telegram. Try again.'),
              },
              false,
              'telegram/unlink/error'
            );
          }
        },

        reset: () => {
          cancelPoll();
          set({ ...initialState }, false, 'telegram/reset');
        },
      };
    },
    { name: 'telegram-store' }
  )
);
